import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, X } from 'lucide-react';
import './ETPOS/CommonStyles.css';
import './ETPOS/AddSellStyles.css';
import './CardForm.css';
import apiService from '../services/api';
import useToast from '../hooks/useToast';
import { usePermissionContext } from '../contexts/PermissionContext';

const AddUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useToast();
  const { hasPermission, permissions } = usePermissionContext();
  
  // Check if we're editing a user
  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  const [formData, setFormData] = useState({
    // Personal Information
    prefix: 'mr',
    first_name: '',
    last_name: '',
    email: '',
    is_active: true,
    enable_service_staff_pin: false,

    // Roles and Permissions
    allow_login: true,
    username: '',
    password: '',
    confirm_password: '',
    role: '',

    // Sales
    cmmsn_percent: '',
    max_discount_percent: '',
    selected_contacts: false,

    // More Informations
    date_of_birth: '',
    gender: '',
    marital_status: '',
    blood_group: '',
    mobile: '',
    alternate_contact: '',
    family_contact: '',
    facebook: '',
    twitter: '',
    social_media_1: '',
    social_media_2: '',
    custom_field_1: '',
    custom_field_2: '',
    custom_field_3: '',
    custom_field_4: '',
    guardian_name: '',
    id_proof_name: '',
    id_proof_number: '',
    permanent_address: '',
    current_address: '',

    // Bank Details
    account_holder_name: '',
    account_number: '',
    bank_name: '',
    bank_identifier_code: '',
    branch: '',
    tax_payer_id: '',

    // HRM Details
    department: '',
    designation: '',

    // Payroll
    primary_work_location: '',
    basic_salary: '',
    pay_period: 'per_month',
    pay_components: '',

    // Misc
    language: 'en'
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Load roles dropdown
  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoadingRoles(true);
        const userDetails = await apiService.getUserDetails();
        const businessId = userDetails?.business_id || 40;
        
        console.log('Loading roles for business_id:', businessId);
        const response = await apiService.getRolesDropdown(businessId);
        console.log('Roles dropdown response:', response);
        
        if (response && Array.isArray(response)) {
          setRoles(response);
        } else if (response.data && Array.isArray(response.data)) {
          setRoles(response.data);
        } else {
          console.error('Invalid roles response format:', response);
          setRoles([]);
        }
      } catch (error) {
        console.error('Error loading roles:', error);
        setRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    };
    
    loadRoles();
  }, []);

  // Load user data for editing
  useEffect(() => {
    if (location.state?.user) {
      const userData = location.state.user;
      console.log('🔥 EDIT USER - User data received:', userData);
      console.log('🔥 EDIT USER - User data keys:', Object.keys(userData));
      setIsEditing(true);
      setEditingUserId(userData.id);
      
      // Populate form with user data
      const mappedData = {
        prefix: userData.prefix || 'mr',
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        is_active: userData.is_active !== undefined ? userData.is_active : true,
        enable_service_staff_pin: userData.enable_service_staff_pin || false,
        allow_login: userData.allow_login !== undefined ? userData.allow_login : true,
        username: userData.username || '',
        password: '', // Don't populate password for security
        confirm_password: '',
        role: userData.role_id || '', // Use role_id from API
        cmmsn_percent: userData.cmmsn_percent || '',
        max_discount_percent: userData.max_sales_discount_percent || '', // Map to correct field name
        selected_contacts: userData.selected_contacts || false,
        date_of_birth: userData.dob || '', // Map to correct field name
        gender: userData.gender || '',
        marital_status: userData.marital_status || '',
        blood_group: userData.blood_group || '',
        mobile: userData.contact_number || '', // Map to correct field name
        alternate_contact: userData.alt_number || '', // Map to correct field name
        family_contact: userData.family_number || '', // Map to correct field name
        facebook: userData.fb_link || '', // Map to correct field name
        twitter: userData.twitter_link || '', // Map to correct field name
        social_media_1: userData.social_media_1 || '',
        social_media_2: userData.social_media_2 || '',
        custom_field_1: userData.custom_field_1 || '',
        custom_field_2: userData.custom_field_2 || '',
        custom_field_3: userData.custom_field_3 || '',
        custom_field_4: userData.custom_field_4 || '',
        guardian_name: userData.guardian_name || '',
        id_proof_name: userData.id_proof_name || '',
        id_proof_number: userData.id_proof_number || '',
        account_holder_name: userData.account_holder_name || '',
        account_number: userData.account_number || '',
        bank_name: userData.bank_name || '',
        bank_identifier_code: userData.bank_identifier_code || '',
        branch: userData.branch || '',
        tax_payer_id: userData.tax_payer_id || '',
        department: userData.department || '',
        designation: userData.designation || '',
        language: userData.language || 'en',
        primary_work_location: userData.primary_work_location || '',
        permanent_address: userData.permanent_address || '',
        current_address: userData.current_address || '',
        basic_salary: userData.basic_salary || '',
        pay_period: userData.pay_period || 'per_month',
        pay_components: userData.pay_components || ''
      };
      
      console.log('🔥 EDIT USER - Mapped form data:', mappedData);
      setFormData(mappedData);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    console.log('🔥 HANDLE SUBMIT CALLED - Event:', e);
    e.preventDefault();
    console.log('🔥 Form submitted:', formData);
    debugger; // Debug: Check form data before API call
    
    // Basic validation
    if (!formData.first_name || !formData.first_name.trim()) {
      showWarning('First Name is required!');
      return;
    }
    
    if (!formData.username || !formData.username.trim()) {
      showWarning('Username is required!');
      return;
    }
    
    if (!isEditing && (!formData.password || !formData.password.trim())) {
      showWarning('Password is required!');
      return;
    }
    
    if (!isEditing && formData.password !== formData.confirm_password) {
      showWarning('Password and Confirm Password do not match!');
      return;
    }
    
    // Check permissions before proceeding
    if (isEditing && !hasPermission('user.update') && permissions.length > 0) {
      showError('You do not have permission to update users');
      return;
    }
    
    if (!isEditing && !hasPermission('user.create') && permissions.length > 0) {
      showError('You do not have permission to create users');
      return;
    }
    
    console.log('ADD USER FORM - Validation and permissions passed, proceeding with API call...');
    
    // Map form data to backend expected field names
    const apiData = {
      business_id: 40, // Default business ID
      prefix: formData.prefix,
      first_name: formData.first_name,
      last_name: formData.last_name || null,
      email: formData.email || null,
      username: formData.username,
      password: formData.password,
      password_confirmation: formData.confirm_password,
      role_id: parseInt(formData.role), // Use the selected role ID directly
      is_active: formData.is_active,
      allow_login: formData.allow_login,
      enable_service_staff_pin: formData.enable_service_staff_pin,
      cmmsn_percent: formData.cmmsn_percent ? parseFloat(formData.cmmsn_percent) : 0, // Default to 0 instead of null
      max_sales_discount_percent: formData.max_discount_percent ? parseFloat(formData.max_discount_percent) : 0, // Default to 0 instead of null
      selected_contacts: formData.selected_contacts ? 1 : 0, // Convert to integer
      dob: formData.date_of_birth || null,
      gender: formData.gender || null,
      marital_status: formData.marital_status || null,
      blood_group: formData.blood_group || null,
      contact_number: formData.mobile || null,
      alt_number: formData.alternate_contact || null,
      family_number: formData.family_contact || null,
      fb_link: formData.facebook || null,
      twitter_link: formData.twitter || null,
      social_media_1: formData.social_media_1 || null,
      social_media_2: formData.social_media_2 || null,
      custom_field_1: formData.custom_field_1 || null,
      custom_field_2: formData.custom_field_2 || null,
      custom_field_3: formData.custom_field_3 || null,
      custom_field_4: formData.custom_field_4 || null,
      guardian_name: formData.guardian_name || null,
      id_proof_name: formData.id_proof_name || null,
      id_proof_number: formData.id_proof_number || null,
      permanent_address: formData.permanent_address || null,
      current_address: formData.current_address || null,
      bank_details: {
        account_holder_name: formData.account_holder_name || null,
        account_number: formData.account_number || null,
        bank_name: formData.bank_name || null,
        bank_identifier_code: formData.bank_identifier_code || null,
        branch: formData.branch || null,
        tax_payer_id: formData.tax_payer_id || null
      },
      department_id: formData.department ? (formData.department === 'sales' ? 1 : formData.department === 'purchase' ? 2 : null) : null,
      designation_id: formData.designation ? (formData.designation === 'supervisor' ? 1 : formData.designation === 'manager' ? 2 : null) : null,
      location_id: formData.primary_work_location ? parseInt(formData.primary_work_location) : null,
      basic_salary: formData.basic_salary ? parseFloat(formData.basic_salary) : 0, // Default to 0
      pay_period: formData.pay_period || 'per_month', // Default pay period
      pay_components: formData.pay_components || null
    };
    
    console.log('ADD USER FORM - Mapped API data:', apiData);
    
    try {
      let response;
      if (isEditing) {
        console.log('ADD USER FORM - Updating user with ID:', editingUserId);
        response = await apiService.updateUser(editingUserId, apiData);
        console.log('ADD USER FORM - User updated successfully:', response);
        
        // Navigate back with success state
        navigate('/users', { state: { activeTab: 'users', userUpdated: true } });
      } else {
        console.log('ADD USER FORM - Creating new user...');
        response = await apiService.createUser(apiData);
        console.log('ADD USER FORM - User created successfully:', response);
        debugger; // Debug: Check API response
        
        // Navigate back with success state
        navigate('/users', { state: { activeTab: 'users', userCreated: true } });
      }
    } catch (error) {
      console.error('ADD USER FORM - Error saving user:', error);
      debugger; // Debug: Check error details
      showError('Failed to save user: ' + error.message);
    }
  };

  return (
    <div className="card-form-container">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button
          onClick={() => navigate('/users', { state: { activeTab: 'users' } })}
          className="btn btn-secondary"
          style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', width: '40px', height: '40px' }}
          title="Back to Users"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
        </button>
        <h3 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>
          {isEditing ? 'Edit User' : 'Add User'}
        </h3>
      </div>

      <form id="userForm" onSubmit={handleSubmit} className="sell-form" style={{ gap: '0.5rem' }}>

        {/* User Information */}
        <div className="form-section" style={{ padding: '0.75rem 1.5rem', marginBottom: '0' }}>
          <h3>User Information</h3>
          <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
            <div className="form-group">
              <label>Prefix</label>
              <select
                className="form-select"
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
              >
                <option value="mr">Mr</option>
                <option value="mrs">Mrs</option>
                <option value="miss">Miss</option>
              </select>
            </div>
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                className="form-input"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                className="form-input"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="form-input"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: 0 }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={!!formData.is_active}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem', width: 'auto', height: 'auto' }}
                />
                Is active?
              </label>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: 0 }}>
                <input
                  type="checkbox"
                  name="enable_service_staff_pin"
                  checked={!!formData.enable_service_staff_pin}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem', width: 'auto', height: 'auto' }}
                />
                Enable service staff pin
              </label>
            </div>
          </div>
        </div>

        {/* Roles and Permissions */}
        <div className="form-section" style={{ padding: '0.75rem 1.5rem', marginBottom: '0' }}>
          <h3>Roles and Permissions</h3>
          <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
            <div className="form-group full-width">
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'bold' }}>
                <input
                  type="checkbox"
                  name="allow_login"
                  checked={!!formData.allow_login}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem', width: 'auto', height: 'auto' }}
                />
                Allow login
              </label>
            </div>
          </div>
          {formData.allow_login && (
            <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  className="form-input"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  className="form-input"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  className="form-input"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  className="form-select"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  disabled={loadingRoles}
                >
                  <option value="">Please Select</option>
                  {loadingRoles ? (
                    <option value="">Loading roles...</option>
                  ) : (
                    roles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Sales */}
        <div className="form-section" style={{ padding: '0.75rem 1.5rem', marginBottom: '0' }}>
          <h3>Sales</h3>
          <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
            <div className="form-group">
              <label>Sales Commission (%):</label>
              <input
                type="text"
                className="form-input"
                name="cmmsn_percent"
                value={formData.cmmsn_percent}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Max discount (%):</label>
              <input
                type="text"
                className="form-input"
                name="max_discount_percent"
                value={formData.max_discount_percent}
                onChange={handleChange}
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: 0 }}>
                <input
                  type="checkbox"
                  name="selected_contacts"
                  checked={!!formData.selected_contacts}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem', width: 'auto', height: 'auto' }}
                />
                Allow Selected Contacts
              </label>
            </div>
          </div>
        </div>

        {/* More Information */}
        <div className="form-section" style={{ padding: '0.75rem 1.5rem', marginBottom: '0' }}>
          <h3>More Information</h3>
          <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
            <div className="form-group">
              <label>Date of birth:</label>
              <input
                type="date"
                className="form-input"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Gender:</label>
              <select
                className="form-select"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Please Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Marital Status:</label>
              <select
                className="form-select"
                name="marital_status"
                value={formData.marital_status}
                onChange={handleChange}
              >
                <option value="">Marital Status</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Blood Group:</label>
              <input
                type="text"
                className="form-input"
                name="blood_group"
                value={formData.blood_group}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Mobile Number:</label>
              <input
                type="text"
                className="form-input"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Alternate contact number:</label>
              <input
                type="text"
                className="form-input"
                name="alternate_contact"
                value={formData.alternate_contact}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Family contact number:</label>
              <input
                type="text"
                className="form-input"
                name="family_contact"
                value={formData.family_contact}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Facebook Link:</label>
              <input
                type="text"
                className="form-input"
                name="facebook"
                value={formData.facebook}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Twitter Link:</label>
              <input
                type="text"
                className="form-input"
                name="twitter"
                value={formData.twitter}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Social Media 1:</label>
              <input
                type="text"
                className="form-input"
                name="social_media_1"
                value={formData.social_media_1}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Social Media 2:</label>
              <input
                type="text"
                className="form-input"
                name="social_media_2"
                value={formData.social_media_2}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Custom field 1:</label>
              <input
                type="text"
                className="form-input"
                name="custom_field_1"
                value={formData.custom_field_1}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Custom field 2:</label>
              <input
                type="text"
                className="form-input"
                name="custom_field_2"
                value={formData.custom_field_2}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Custom field 3:</label>
              <input
                type="text"
                className="form-input"
                name="custom_field_3"
                value={formData.custom_field_3}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Custom field 4:</label>
              <input
                type="text"
                className="form-input"
                name="custom_field_4"
                value={formData.custom_field_4}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Guardian Name:</label>
              <input
                type="text"
                className="form-input"
                name="guardian_name"
                value={formData.guardian_name}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>ID proof name:</label>
              <input
                type="text"
                className="form-input"
                name="id_proof_name"
                value={formData.id_proof_name}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>ID proof number:</label>
              <input
                type="text"
                className="form-input"
                name="id_proof_number"
                value={formData.id_proof_number}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Permanent Address:</label>
              <textarea
                className="form-textarea"
                name="permanent_address"
                value={formData.permanent_address}
                onChange={handleChange}
                rows="2"
              />
            </div>
            <div className="form-group">
              <label>Current Address:</label>
              <textarea
                className="form-textarea"
                name="current_address"
                value={formData.current_address}
                onChange={handleChange}
                rows="2"
              />
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="form-section" style={{ padding: '0.75rem 1.5rem', marginBottom: '0' }}>
          <h3>Bank Details</h3>
          <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
            <div className="form-group">
              <label>Account Holder's Name:</label>
              <input
                type="text"
                className="form-input"
                name="account_holder_name"
                value={formData.account_holder_name}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Account Number:</label>
              <input
                type="text"
                className="form-input"
                name="account_number"
                value={formData.account_number}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Bank Name:</label>
              <input
                type="text"
                className="form-input"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Bank Identifier Code:</label>
              <input
                type="text"
                className="form-input"
                name="bank_identifier_code"
                value={formData.bank_identifier_code}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Branch:</label>
              <input
                type="text"
                className="form-input"
                name="branch"
                value={formData.branch}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Tax Payer ID:</label>
              <input
                type="text"
                className="form-input"
                name="tax_payer_id"
                value={formData.tax_payer_id}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* HRM Details */}
        <div className="form-section" style={{ padding: '0.75rem 1.5rem', marginBottom: '0' }}>
          <h3>HRM Details</h3>
          <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
            <div className="form-group">
              <label>Department *</label>
              <select
                className="form-select"
                name="department"
                value={formData.department}
                onChange={handleChange}
                required
              >
                <option value="">Please Select</option>
                <option value="sales">Sales</option>
                <option value="purchase">Purchase</option>
                <option value="accounts">Accounts</option>
                <option value="hr">HR</option>
              </select>
            </div>
            <div className="form-group">
              <label>Designation *</label>
              <select
                className="form-select"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                required
              >
                <option value="">Please Select</option>
                <option value="staff">Staff</option>
                <option value="supervisor">Supervisor</option>
                <option value="manager">Manager</option>
              </select>
            </div>
          </div>
        </div>

        {/* Payroll */}
        <div className="form-section" style={{ padding: '0.75rem 1.5rem', marginBottom: '0' }}>
          <h3>Payroll</h3>
          <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
            <div className="form-group">
              <label>Primary work location:</label>
              <select
                className="form-select"
                name="primary_work_location"
                value={formData.primary_work_location}
                onChange={handleChange}
              >
                <option value="">Please Select</option>
                <option value="main">Main</option>
                <option value="branch">Branch</option>
              </select>
            </div>
            <div className="form-group">
              <label>Basic salary:</label>
              <input
                type="text"
                className="form-input"
                name="basic_salary"
                value={formData.basic_salary}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Pay Period:</label>
              <select
                className="form-select"
                name="pay_period"
                value={formData.pay_period}
                onChange={handleChange}
              >
                <option value="per_month">Per Month</option>
                <option value="per_week">Per Week</option>
                <option value="per_day">Per Day</option>
              </select>
            </div>
            <div className="form-group">
              <label>Pay Components:</label>
              <input
                type="text"
                className="form-input"
                name="pay_components"
                value={formData.pay_components}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="action-buttons-section" style={{ margin: '1rem', padding: '0 0.5rem' }}>
          <div className="action-buttons" style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button
              type="submit"
              className="save-btn"
              onClick={(e) => {
                console.log('🔥 SAVE BUTTON CLICKED!');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1.25rem',
                backgroundColor: '#3b82f6',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              <Save size={16} /> {isEditing ? 'Update User' : 'Save User'}
            </button>
          </div>
        </div>
      </form>

    </div>
  );
};

export default AddUser;